import React, { useState, useEffect, useCallback, useRef } from "react";
import customFetch from "../apicall/api";
import { logout } from "../Utility";
import { fmtINR, STATUS_COLOUR } from "./helpers";
import FilterDrawer from "./FilterDrawer";
import TransactionCard from "./TransactionCard";
import "./TransactionMonitor.css";

// A standalone NDLC entry = individual household cash collection not part of any batch
function isStandaloneNDLC(txn) {
  return (
    (txn.reference_number || "").startsWith("NDLC") &&
    (!txn.txn_ref_no || txn.txn_ref_no.startsWith("NDLC"))
  );
}

var PAGE_SIZE = 50;

function todayStr() {
  var d = new Date();
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0")
  );
}

function fmtDateLabel(str) {
  if (!str) return "Today";
  if (str === todayStr()) return "Today";
  var d = new Date(str + "T00:00:00");
  if (isNaN(d)) return str;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

var TODAY = todayStr();

var DEFAULT_FILTERS = {
  involvement: "all",
  status: "",
  collection_mode: "",
  start_date: TODAY,
  end_date: TODAY,
};

function hasActiveFilter(filters) {
  return (
    filters.involvement !== "all" ||
    !!filters.status ||
    !!filters.collection_mode ||
    filters.start_date !== TODAY ||
    filters.end_date !== TODAY
  );
}

// ── Cash Wallet Tab ───────────────────────────────────────────────────────────

function CashWalletTab({ ndlcTxns, metrics, apiToken }) {
  var uid      = localStorage.getItem("in_userid");
  var userName = localStorage.getItem("in_username") || "Me";
  var empId    = uid ? "EMP-" + uid : "—";
  var [expandedId, setExpandedId] = useState(null);

  function txnAmt(t) { return t.total_amount_collected || t.amount_collected || 0; }

  var collected = ndlcTxns.reduce(function(s, t) { return s + txnAmt(t); }, 0);
  var submitted = ndlcTxns
    .filter(function(t) { return t.status_code >= 90; })
    .reduce(function(s, t) { return s + txnAmt(t); }, 0);
  var holding   = metrics ? (metrics.holding_total || 0) : Math.max(0, collected - submitted);

  return (
    <div className="tm-wallet-root">
      <div className="tm-wallet-hero">
        <span className="tm-wallet-hero__icon">🏦</span>
        <div>
          <div className="tm-wallet-hero__label">Remaining Balance</div>
          <div className="tm-wallet-hero__amount">₹{fmtINR(holding)}</div>
          <div className="tm-wallet-hero__name">{userName} · {empId}</div>
        </div>
      </div>

      <div className="tm-wallet-stats">
        <div className="tm-wallet-stat">
          <div className="tm-wallet-stat__icon">⊕</div>
          <div className="tm-wallet-stat__label">Opening</div>
          <div className="tm-wallet-stat__val">₹0</div>
        </div>
        <div className="tm-wallet-stat">
          <div className="tm-wallet-stat__icon">💰</div>
          <div className="tm-wallet-stat__label">Collected</div>
          <div className="tm-wallet-stat__val">₹{fmtINR(collected)}</div>
        </div>
        <div className="tm-wallet-stat">
          <div className="tm-wallet-stat__icon">↑</div>
          <div className="tm-wallet-stat__label">Submitted</div>
          <div className="tm-wallet-stat__val">₹{fmtINR(submitted)}</div>
        </div>
      </div>

      <div className="tm-section-title" style={{ margin: "12px 16px 4px" }}>CASH TRANSACTIONS</div>
      {ndlcTxns.length === 0 ? (
        <div className="tm-empty"><div className="tm-empty__title">No cash transactions.</div></div>
      ) : (
        <div className="tm-list">
          {ndlcTxns.map(function(txn, idx) {
            var cardId = (txn.reference_number || txn.txn_ref_no || "") + "_" + idx;
            return (
              <TransactionCard
                key={cardId}
                txn={txn}
                expanded={expandedId === cardId}
                onToggle={function() {
                  setExpandedId(function(prev) { return prev === cardId ? null : cardId; });
                }}
                apiToken={apiToken}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Agents Tab ────────────────────────────────────────────────────────────────

function AgentsTab({ transactions }) {
  var branchMap = {};
  transactions.forEach(function(t) {
    var branch = t.branch_name || "Unknown Branch";
    if (!branchMap[branch]) {
      branchMap[branch] = { total: 0, pending: 0, count: 0 };
    }
    branchMap[branch].total  += (t.amount_collected || 0);
    branchMap[branch].count  += 1;
    if (t.status_code < 100) branchMap[branch].pending += (t.amount_collected || 0);
  });
  var branches = Object.entries(branchMap)
    .map(function(e) { return { name: e[0], total: e[1].total, pending: e[1].pending, count: e[1].count }; })
    .sort(function(a, b) { return b.total - a.total; });

  return (
    <div className="tm-agents-root">
      <div className="tm-section-title" style={{ margin: "12px 16px 4px" }}>BRANCH-WISE COLLECTIONS</div>
      {branches.length === 0 ? (
        <div className="tm-empty"><div className="tm-empty__title">No branch data available.</div></div>
      ) : branches.map(function(branch, i) {
        return (
          <div key={i} className="tm-agent-row">
            <div className="tm-agent-row__info">
              <div className="tm-agent-row__name">{branch.name}</div>
              <div className="tm-agent-row__empid">{branch.count} transaction{branch.count !== 1 ? "s" : ""}</div>
            </div>
            <div className="tm-agent-row__right">
              <div className="tm-agent-row__total">₹{fmtINR(branch.total)}</div>
              {branch.pending > 0 && (
                <div className="tm-agent-row__pending">Pending: ₹{fmtINR(branch.pending)}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function TransactionMonitor({ history, location }) {
  var api_token =
    localStorage.getItem("api_token") ||
    (location && location.state && location.state.api_token);
  var uid        = localStorage.getItem("in_userid");
  var branchName = localStorage.getItem("in_branch_name") || "";

  var [transactions, setTransactions] = useState([]);
  var [filtered, setFiltered]         = useState([]);
  var [loading, setLoading]           = useState(false);
  var [loadingMore, setLoadingMore]   = useState(false);
  var [error, setError]               = useState(null);
  var [searchText, setSearchText]     = useState("");
  var [expandedId, setExpandedId]     = useState(null);
  var [showFilter, setShowFilter]     = useState(false);
  var [filters, setFilters]           = useState(DEFAULT_FILTERS);
  var [metrics, setMetrics]           = useState(null);
  var [hasMore, setHasMore]           = useState(false);
  var [skip, setSkip]                 = useState(0);
  var [activeTab, setActiveTab]       = useState("transactions");

  var filtersRef = useRef(filters);
  filtersRef.current = filters;

  // ── Fetch ─────────────────────────────────────────────────────────────────

  var fetchList = useCallback(
    function(activeFilters, skipOffset, append) {
      if (!uid) { logout(); return; }
      if (append) { setLoadingMore(true); }
      else        { setLoading(true); setError(null); }

      var params = new URLSearchParams({
        userid:      uid,
        involvement: activeFilters.involvement,
        skip:        skipOffset || 0,
        page_size:   PAGE_SIZE,
      });
      if (activeFilters.status)          params.set("status", activeFilters.status);
      if (activeFilters.collection_mode) params.set("collection_mode", activeFilters.collection_mode);
      if (activeFilters.start_date)      params.set("start_date", activeFilters.start_date);
      if (activeFilters.end_date)        params.set("end_date", activeFilters.end_date);

      customFetch(
        process.env.REACT_APP_URL + "/reversal/monitor/list?" + params.toString(),
        { method: "GET", headers: { "api-token": api_token, in_userid: uid } }
      )
        .then(function(res) { return res.json(); })
        .then(function(json) {
          if (json.api_code === 4 || json.api_code === 3 || json.api_code === 5) {
            localStorage.clear();
            history.push("/");
            return;
          }
          var list = json.data || [];
          if (append) {
            setTransactions(function(prev) { return prev.concat(list); });
          } else {
            setTransactions(list);
            setMetrics(json.metrics || null);
          }
          setHasMore(!!json.has_more);
          setSkip((skipOffset || 0) + list.length);
        })
        .catch(function(e) {
          console.error("TransactionMonitor fetch error:", e);
          setError("Could not load transactions.");
        })
        .finally(function() {
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [uid, api_token, history]
  );

  useEffect(function() {
    fetchList(DEFAULT_FILTERS, 0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Client-side search ────────────────────────────────────────────────────

  useEffect(function() {
    var q = searchText.trim().toUpperCase();
    // Transactions tab shows only non-standalone-NDLC entries
    var mainTxns = transactions.filter(function(t) { return !isStandaloneNDLC(t); });
    if (!q) { setFiltered(mainTxns); return; }
    setFiltered(
      mainTxns.filter(function(t) {
        return (
          (t.txn_ref_no_bank  || "").toUpperCase().includes(q) ||
          (t.txn_ref_no       || "").toUpperCase().includes(q) ||
          (t.household_name   || "").toUpperCase().includes(q) ||
          (t.household_number || "").toUpperCase().includes(q)
        );
      })
    );
  }, [searchText, transactions]);

  function applyFilters(newFilters) {
    setFilters(newFilters);
    setSkip(0);
    fetchList(newFilters, 0, false);
  }

  function loadMore() { fetchList(filtersRef.current, skip, true); }
  function retry()    { setError(null); fetchList(filtersRef.current, 0, false); }

  var filterActive = hasActiveFilter(filters);

  // ── KPI calculations ──────────────────────────────────────────────────────

  var totalCollection = metrics
    ? (metrics.handover_received_total || 0)
    : transactions.reduce(function(s, t) { return s + (t.amount_collected || 0); }, 0);
  var cashPending = metrics
    ? (metrics.holding_total || 0)
    : transactions
        .filter(function(t) { return t.status_code < 90; })
        .reduce(function(s, t) { return s + (t.amount_collected || 0); }, 0);
  var bankDeposited = metrics
    ? (metrics.deposit_done_total || 0)
    : transactions
        .filter(function(t) { return t.status_code >= 100; })
        .reduce(function(s, t) { return s + (t.amount_collected || 0); }, 0);
  var reconPending = transactions
    .filter(function(t) { return t.status_code >= 100 && t.status_code < 110; })
    .reduce(function(s, t) { return s + (t.amount_collected || 0); }, 0);

  var ndlcTxns = transactions.filter(function(t) { return isStandaloneNDLC(t); });

  var dateLabel =
    filters.start_date === filters.end_date
      ? fmtDateLabel(filters.start_date)
      : fmtDateLabel(filters.start_date) + " – " + fmtDateLabel(filters.end_date);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="tm-root">
      {/* Navbar */}
      <div className="tm-navbar">
        <button
          className="tm-navbar__back"
          onClick={function() { history && history.goBack(); }}
        >
          ‹
        </button>
        <div className="tm-navbar__center">
          <span className="tm-navbar__title">Collection Dashboard</span>
          <span className="tm-navbar__subtitle">
            {branchName ? branchName + " · " : ""}{dateLabel}
          </span>
        </div>
        <button className="tm-navbar__logout" onClick={function() { logout(); }}>
          ⏻
        </button>
      </div>

      {/* KPI Cards */}
      {!loading && (
        <div className="tm-kpi-grid">
          <div className="tm-kpi-card">
            <div className="tm-kpi-card__icon">₹</div>
            <div className="tm-kpi-card__label">Total Collection</div>
            <div className="tm-kpi-card__value">₹{fmtINR(totalCollection)}</div>
          </div>
          <div className="tm-kpi-card tm-kpi-card--orange">
            <div className="tm-kpi-card__icon">⏱</div>
            <div className="tm-kpi-card__label">Cash Pending</div>
            <div className="tm-kpi-card__value">₹{fmtINR(cashPending)}</div>
          </div>
          <div className="tm-kpi-card tm-kpi-card--green">
            <div className="tm-kpi-card__icon">🏦</div>
            <div className="tm-kpi-card__label">Bank Deposited</div>
            <div className="tm-kpi-card__value">₹{fmtINR(bankDeposited)}</div>
          </div>
          <div className="tm-kpi-card tm-kpi-card--teal">
            <div className="tm-kpi-card__icon">↻</div>
            <div className="tm-kpi-card__label">Recon Pending</div>
            <div className="tm-kpi-card__value">₹{fmtINR(reconPending)}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tm-tabs">
        <button
          className={"tm-tab" + (activeTab === "transactions" ? " tm-tab--active" : "")}
          onClick={function() { setActiveTab("transactions"); }}
        >
          Transactions
        </button>
        <button
          className={"tm-tab" + (activeTab === "wallet" ? " tm-tab--active" : "")}
          onClick={function() { setActiveTab("wallet"); }}
        >
          Cash Wallet
        </button>
        <button
          className={"tm-tab" + (activeTab === "agents" ? " tm-tab--active" : "")}
          onClick={function() { setActiveTab("agents"); }}
        >
          Agents
        </button>
      </div>

      {/* ── Transactions tab ─────────────────────────────────────────────── */}
      {activeTab === "transactions" && (
        <>
          {/* Search + Filter bar */}
          <div className="tm-searchbar">
            <div className="tm-searchbar__icon">🔍</div>
            <input
              className="tm-searchbar__input"
              type="text"
              placeholder="Search name, loan ID, ref..."
              value={searchText}
              onChange={function(e) { setSearchText(e.target.value); }}
            />
            <button
              className={"tm-searchbar__filter-btn" + (filterActive ? " tm-searchbar__filter-btn--active" : "")}
              onClick={function() { setShowFilter(true); }}
              title="Filters"
            >
              ⊞{filterActive && <span className="tm-filter-dot" />}
            </button>
          </div>

          {error && (
            <div className="tm-error-banner">
              <span className="tm-error-banner__msg">{error}</span>
              <button className="tm-error-banner__retry" onClick={retry}>Retry</button>
            </div>
          )}

          {!loading && !error && (
            <div className="tm-count">
              {filtered.length} transaction{filtered.length !== 1 ? "s" : ""}
            </div>
          )}

          {loading && <div className="preloader"><div className="loader" /></div>}

          {!loading && !error && filtered.length === 0 && (
            <div className="tm-empty">
              <div className="tm-empty__title">No transactions found.</div>
              {(searchText || filterActive) && (
                <div className="tm-empty__sub">Try adjusting your filters or search.</div>
              )}
            </div>
          )}

          <div className="tm-list">
            {filtered.map(function(txn, idx) {
              var cardId =
                (txn.txn_ref_no_bank || txn.reference_number || txn.txn_ref_no) + "_" + idx;
              return (
                <TransactionCard
                  key={cardId}
                  txn={txn}
                  expanded={expandedId === cardId}
                  onToggle={function() {
                    setExpandedId(function(prev) { return prev === cardId ? null : cardId; });
                  }}
                  apiToken={api_token}
                />
              );
            })}
          </div>

          {hasMore && !loading && !error && (
            <div className="tm-load-more-wrap">
              <button className="tm-load-more" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load More"}
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Cash Wallet tab ──────────────────────────────────────────────── */}
      {activeTab === "wallet" && (
        <CashWalletTab ndlcTxns={ndlcTxns} metrics={metrics} apiToken={api_token} />
      )}

      {/* ── Agents tab ───────────────────────────────────────────────────── */}
      {activeTab === "agents" && (
        <AgentsTab transactions={transactions} />
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        visible={showFilter}
        onClose={function() { setShowFilter(false); }}
        filters={filters}
        onChange={applyFilters}
      />
    </div>
  );
}
